import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useMemo,
} from "react";
import { createPortal } from "react-dom";
import { useTheme } from "../../../theme/ThemeContext";
import "./SpecialityCombobox.css";

/**
 * SpecialityCombobox — searchable, grouped speciality picker (variant A).
 *
 * Props: { value, onChange(value), options, groups, disabled, error, id }
 *   options — [{ value, label, group }]   (injected; catalogue not imported)
 *   groups  — [{ id, label }]             (display order)
 * Emits a known option code, or the free text typed under "Other (type it)".
 * An unknown incoming `value` opens as Other with the text prefilled.
 */
const PANEL_MAX = 320;
const PANEL_MIN = 200;
const GAP = 4;
const EDGE = 8;
const PANEL_Z = 1100; // above the staff modal (z 910)

const highlight = (label, q) => {
  if (!q) return label;
  const i = label.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return label;
  return (
    <>
      {label.slice(0, i)}
      <mark>{label.slice(i, i + q.length)}</mark>
      {label.slice(i + q.length)}
    </>
  );
};

const SpecialityCombobox = ({
  value = "",
  onChange,
  options = [],
  groups = [],
  disabled = false,
  error,
  id = "speciality-combobox",
}) => {
  const { theme } = useTheme();
  const known = options.find((o) => o.value === value);
  // Seeded ONCE: an unknown stored value is Other, text prefilled (never blank).
  const [otherMode, setOtherMode] = useState(() => !!value && !known);
  const [otherText, setOtherText] = useState(() =>
    value && !known ? value : "",
  );
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const [pos, setPos] = useState(null);
  const inputRef = useRef(null);
  const rootRef = useRef(null);
  const otherRef = useRef(null);
  const listRef = useRef(null);

  const q = query.trim().toLowerCase();
  const sections = useMemo(
    () =>
      groups
        .map((g) => ({
          ...g,
          items: options.filter(
            (o) =>
              o.group === g.id &&
              (!q ||
                o.label.toLowerCase().includes(q) ||
                (o.keywords || "").toLowerCase().includes(q) ||
                g.label.toLowerCase().includes(q)),
          ),
        }))
        .filter((g) => g.items.length > 0),
    [groups, options, q],
  );
  const flat = useMemo(() => sections.flatMap((s) => s.items), [sections]);
  const otherIndex = flat.length; // "Other" is pinned last

  // Panel is portalled + position:fixed so table/modal overflow can't clip it.
  useLayoutEffect(() => {
    if (!open) return undefined;
    const place = () => {
      if (!inputRef.current) return;
      const r = inputRef.current.getBoundingClientRect();
      const vh = window.innerHeight;
      const below = vh - r.bottom - EDGE;
      const above = r.top - EDGE;
      const up = below < PANEL_MAX && above > below;
      const maxHeight = Math.max(
        PANEL_MIN,
        Math.min(PANEL_MAX, up ? above : below),
      );
      setPos({
        up,
        left: r.left,
        width: r.width,
        maxHeight,
        ...(up
          ? { bottom: vh - r.top + GAP }
          : { top: r.bottom + GAP }),
      });
    };
    const onScroll = (e) => {
      if (listRef.current && listRef.current.contains(e.target)) return;
      place();
    };
    place();
    window.addEventListener("scroll", onScroll, { capture: true, passive: true });
    window.addEventListener("resize", place, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll, { capture: true });
      window.removeEventListener("resize", place);
    };
  }, [open]);

  useEffect(() => {
    const el =
      listRef.current && listRef.current.querySelector('[data-active="true"]');
    if (el && el.scrollIntoView) el.scrollIntoView({ block: "nearest" });
  }, [active, open]);

  useEffect(() => {
    if (otherMode && otherRef.current && !disabled) otherRef.current.focus();
  }, [otherMode, disabled]);

  const openPanel = () => {
    if (disabled || open) return;
    setQuery("");
    setActive(0);
    setOpen(true);
  };
  const close = () => {
    setOpen(false);
    setQuery("");
  };
  const pick = (idx) => {
    if (idx === otherIndex) {
      setOtherMode(true);
      setOtherText("");
      onChange("");
    } else {
      setOtherMode(false);
      setOtherText("");
      onChange(flat[idx].value);
    }
    close();
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      if (open) {
        e.preventDefault();
        e.stopPropagation();
        close();
      }
    } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) return openPanel();
      const n = flat.length + 1;
      setActive((a) => (e.key === "ArrowDown" ? (a + 1) % n : (a - 1 + n) % n));
    } else if (e.key === "Enter" && open) {
      e.preventDefault();
      pick(active > otherIndex ? otherIndex : active);
    }
  };

  const display = otherMode ? "Other (type it)" : known ? known.label : "";
  const optId = (i) => `${id}-opt-${i}`;
  const border = error ? theme.brand.error : theme.border.input;
  let idx = -1;
  const optionProps = (i, selected) => ({
    id: optId(i),
    role: "option",
    "aria-selected": selected,
    "data-active": i === active,
    onMouseDown: (e) => {
      e.preventDefault();
      pick(i);
    },
    onMouseEnter: () => setActive(i),
    style:
      i === active
        ? { backgroundColor: theme.brand.purple, color: "#fff" }
        : { color: theme.text.primary },
  });

  return (
    <div
      className="spec-cb"
      ref={rootRef}
      onBlur={(e) => {
        if (!rootRef.current.contains(e.relatedTarget)) close();
      }}
    >
      <input
        id={id}
        ref={inputRef}
        type="text"
        className="spec-cb-input"
        role="combobox"
        aria-expanded={open}
        aria-controls={`${id}-listbox`}
        aria-autocomplete="list"
        aria-activedescendant={open ? optId(active) : undefined}
        aria-invalid={!!error}
        autoComplete="off"
        disabled={disabled}
        placeholder={
          open ? "Search specializations..." : "Select specialization"
        }
        value={open ? query : display}
        onFocus={openPanel}
        onClick={openPanel}
        onChange={(e) => {
          setQuery(e.target.value);
          setActive(0);
          setOpen(true);
        }}
        onKeyDown={onKeyDown}
        style={{
          backgroundColor: theme.background.surface,
          color: theme.text.primary,
          borderColor: border,
        }}
      />
      {open &&
        pos &&
        createPortal(
        <div
          className="spec-cb-panel"
          // keep input focus (no blur-close) on any click inside the panel,
          // incl. its scrollbar; portal clicks are never "outside".
          onMouseDown={(e) => e.preventDefault()}
          style={{
            top: pos.top,
            bottom: pos.bottom,
            left: pos.left,
            width: pos.width,
            maxHeight: pos.maxHeight,
            zIndex: PANEL_Z,
            backgroundColor: theme.background.primary,
            borderColor: theme.border.input,
          }}
        >
          <div
            className="spec-cb-count"
            style={{
              color: theme.text.secondary,
              borderColor: theme.border.input,
            }}
          >
            {q
              ? `${flat.length} of ${options.length}`
              : `${options.length} specializations`}
          </div>
          <div
            className="spec-cb-list"
            role="listbox"
            id={`${id}-listbox`}
            ref={listRef}
          >
            {sections.map((s) => (
              <div key={s.id} role="group" aria-label={s.label}>
                <div
                  className="spec-cb-group-head"
                  style={{
                    backgroundColor: theme.background.primary,
                    color: theme.text.secondary,
                    borderColor: theme.border.input,
                  }}
                >
                  {s.label} ({s.items.length})
                </div>
                {s.items.map((o) => {
                  idx += 1;
                  const selected = !otherMode && o.value === value;
                  return (
                    <div
                      key={o.value}
                      className={`spec-cb-option${selected ? " spec-cb-option--selected" : ""}`}
                      {...optionProps(idx, selected)}
                    >
                      {highlight(o.label, query.trim())}
                      {selected ? " ✓" : ""}
                    </div>
                  );
                })}
              </div>
            ))}
            {flat.length === 0 && (
              <div
                className="spec-cb-empty"
                style={{ color: theme.text.secondary }}
              >
                No specialization matches “{query.trim()}”. Not listed? Choose
                “Other (type it)” below.
              </div>
            )}
            <div
              className="spec-cb-option spec-cb-other"
              {...optionProps(otherIndex, otherMode)}
              style={{
                backgroundColor: theme.background.primary,
                ...optionProps(otherIndex, otherMode).style,
                borderColor: theme.border.input,
              }}
            >
              + Other (type it)
            </div>
          </div>
        </div>,
        document.body,
      )}
      {otherMode && (
        <div className="spec-cb-other-wrap">
          <label htmlFor={`${id}-other`} style={{ color: theme.text.primary }}>
            Speciality (as it should print){" "}
            <span className="spec-cb-required">*</span>
          </label>
          <input
            id={`${id}-other`}
            ref={otherRef}
            type="text"
            className="spec-cb-input"
            required
            maxLength={60}
            disabled={disabled}
            placeholder="e.g. Sleep Medicine"
            value={otherText}
            onChange={(e) => {
              setOtherText(e.target.value);
              onChange(e.target.value);
            }}
            style={{
              backgroundColor: theme.background.surface,
              color: theme.text.primary,
              borderColor: border,
            }}
          />
        </div>
      )}
      {error && (
        <span className="spec-cb-error" style={{ color: theme.brand.error }}>
          {error}
        </span>
      )}
    </div>
  );
};

export default SpecialityCombobox;
